/*
  Selector.jsx

  Meant to replicate the MUI Select field.
*/

// Npm.
import React, { useMemo } from 'react';

// Hooks.
import useId from '../../hooks/useId';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Constants.
const NOOP = () => {};

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    flexDirection: 'column-reverse',
    position: 'relative',
    verticalAlign: 'middle',
    color: theme.color_text_primary,
    '&.fullWidth': {
      display: 'flex',
      width: '100%',
    },
    '&.small > select': {
      fontSize: '0.875rem',
      padding: theme.space(0.5, 1),
    },
    '& > select': {
      border: 'none',
      borderRadius: '4px',
      background: 'none',
      boxShadow: `0 0 0 1px ${theme.color_divider} inset`,
      outline: 'none',
      margin: 0,
      minWidth: 0,
      width: '100%',
      padding: theme.space(1),
      fontSize: '1rem',
      fontWeight: 500,
      color: theme.color_text_primary,
      '&::placeholder': {
        color: 'currentColor',
        opacity: 0.2,
      },
      '&:hover': {
        boxShadow: `0 0 0 1px ${theme.color_text_secondary} inset`,
      },
      '&:focus': {
        boxShadow: `0 0 0 2px ${theme.color_primary_main} inset`,
        '& + label': {
          color: theme.color_primary_main,
        }
      },
      '&:disabled': {
        boxShadow: `0 0 0 1px ${theme.color_divider} inset`,
        color: theme.color_text_disabled,
        '& + label': {
          color: theme.color_text_disabled,
        }
      },
      '& optgroup': {
        fontSize: '0.75rem',
        fontStyle: 'normal',
        color: theme.color_text_secondary,
        backgroundColor: theme.color_background,
        '&:disabled': {
          color: theme.color_text_disabled,
        }
      },
      '& option': {
        color: theme.color_text_primary,
        backgroundColor: theme.color_background,
        fontSize: '1rem',
        '&:disabled': {
          color: theme.color_text_disabled,
        }
      },
    },
  },
}));

/**
 * Select field component.
 */
export default function Selector({

  // Selector props.
  className,
  label,
  fullWidth = false,
  size,

  // Input props.
  type = 'text',
  placeholder,
  defaultValue,
  value,
  disabled,
  readOnly,
  autoFocus,
  required,

  // Input callbacks.
  onChange,
  onKeyDown,
  onKeyPress,
  onKeyUp,
  onFocus,
  onBlur,

  children,
  ...rest

}) {

  const id = useId();
  const inputId = `${id}-select`;

  let rootClass = 'Selector';

  if (size === 'small' || size === 'large') {
    rootClass += ` ${size}`;
  }

  if (fullWidth) {
    rootClass += ' fullWidth';
  }

  const classes = useStyles();
  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  return (
    <div className={cls} {...rest}>
      <select
        id={inputId}
        type={type}
        placeholder={placeholder}
        defaultValue={defaultValue}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        required={required}
        onChange={readOnly ? NOOP : onChange}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onKeyPress={onKeyPress}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        { children }
      </select>
      {
        Boolean(label) &&
        <label className="caption" htmlFor={inputId}>{ label }</label>
      }
    </div>
  );
}
